import { ApplicationConfigWithPages } from '../../../shared/features/ng-redux/app/appConfig.models';

import {
  BarChartConfig,
  ChartTypeEnum,
  DonutChartConfig,
  MostLeastEnum,
  TableConfig,
  TimelineChartConfig,
} from '../../../shared/models/chart.models';
import { InsSelectionType, TableRenderEnum } from '@nx-fe/table';
import {
  APP_SPOTLIGHT_INFO_PAGE,
  APP_SPOTLIGHT_LIST_PAGE,
  CHART_UI_BAR_TOPN,
  CHART_UI_BAR_TOPN_MANY,
  CHART_UI_DONUT_LEAST_MOST,
  CHART_UI_DONUT_TOPN,
  DEFAULT_APP_FILTER_BAR_CONFIG,
  DEVICE_APP,
  DEVICE_APP_W,
  DEVICE_LIST,
  DEVICE_LIST_W,
} from './app-mapper.constants';

import { buildChartOptions, findPageId, patchCard, patchChart, patchPage } from './app-mapper.utils';
import { PageCategory } from '../../../shared/features/ng-redux/page/page.models';
import { HighchartsConstant, ColorConstants } from '../../../../../../xs-colors';
import { CONSTANTS } from '../../../shared/localizations/dictionary';
import { ProfilePermissionEnum } from '@nx-fe/services';
export function mapAppSpotlightConfig(
  app: ApplicationConfigWithPages,
  devApp: ApplicationConfigWithPages
): ApplicationConfigWithPages {
  app.group = 'device_filter_app_group';
  app.name = 'as_name';
  app.icon = 'insight-icon-application-list';
  app.dashboardType = CONSTANTS.DASHBOARD_TYPES.DEVICE;
  app.claims = [
    ProfilePermissionEnum.ProfileViewAppUsageDashboard,
    ProfilePermissionEnum.ProfileViewAppUsageShiftDashboard,
  ];
  patchPage(app, APP_SPOTLIGHT_LIST_PAGE, page => {
    page.claims = [
      ProfilePermissionEnum.ProfileViewAppUsageShiftDashboard,
      ProfilePermissionEnum.ProfileViewAppUsageDashboard,
    ];
    page.filterBarVisible = true;
    page.filterBarConfig = DEFAULT_APP_FILTER_BAR_CONFIG;
    page.pageCategory = PageCategory.APP;
    page.shiftEnabled = false;
    page.canManageApps = true;
    page.dateFilterTypes = ['Custom', 'Today', 'Yesterday', 'Last Week', 'Last Month', 'Last 7 Days', 'Last 30 Days'];

    patchChart(page.charts, 'as-appTable', (chart: TableConfig) => {
      chart.name = 'as_table_app_name';
      chart.fullName = 'as_table_app_fullname';

      chart.filters = {
        sortAttribute: 'name',
        sortDirection: 'asc',
        pageSize: 50,
        pageSkip: 0,
      };

      chart.tableAttributes = [
        {
          id: 'name',
          label: 'xs_name_label',
          renderer: TableRenderEnum.LINK,
          rendererParams: {
            eventId: 'appClicked',
          },
        },
        {
          id: 'id',
          label: 'as_table_app_column_id_label',
          renderer: TableRenderEnum.STRING,
        },
        {
          id: 'category',
          label: 'as_table_app_column_category_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'installs',
          renderer: TableRenderEnum.NUMBER,
          label: 'as_table_app_column_installs_label',
        },
        {
          id: 'updates',
          renderer: TableRenderEnum.NUMBER,
          label: 'as_table_app_column_updates_label',
        },
        {
          id: 'visits',
          renderer: TableRenderEnum.NUMBER,
          label: 'as_table_app_column_visits_label',
        },
        {
          id: 'foregroundTime',
          renderer: TableRenderEnum.TIME,
          label: 'foreground_usage_label',
        },
      ];
    });
  });

  patchPage(app, APP_SPOTLIGHT_INFO_PAGE, page => {
    page.claims = [
      ProfilePermissionEnum.ProfileViewAppUsageShiftDashboard,
      ProfilePermissionEnum.ProfileViewAppUsageDashboard,
    ];
    page.filterBarVisible = true;
    page.filterBarConfig = DEFAULT_APP_FILTER_BAR_CONFIG;
    page.pageCategory = PageCategory.APP;
    page.shiftEnabled = true;
    page.canManageApps = true;
    page.childMcGroups = true;
    page.dateFilterTypes = ['Custom', 'Today', 'Yesterday', 'Last Week', 'Last Month', 'Last 7 Days', 'Last 30 Days'];

    // Filter out as-*Shift charts - these are not currently enabled
    page.charts = page.charts.filter(chart => !['as-foregroundShift', 'as-visitShift'].includes(chart.baselineId));

    page.predefinedCharts = [
      {
        category: {
          name: 'as_category_device',
          icon: 'insight-icon-device',
          tooltip: 'aps_category_device_tip',
        },
        charts: [
          buildChartOptions(page.charts, 'as-manufacturer', 'as_chart_predefined_manufacturer_name'),
          buildChartOptions(page.charts, 'as-model', 'as_chart_predefined_model_name'),
          buildChartOptions(page.charts, 'as-osVersion', 'as_chart_predefined_osVersion_name'),
        ],
      },
      {
        category: {
          name: 'as_category_application',
          icon: '',
          tooltip: 'aps_category_app_tip',
        },
        charts: [
          buildChartOptions(page.charts, 'as-deviceStats', 'as_chart_deviceStats_name'),
          buildChartOptions(page.charts, 'as-usage-status', 'as_chart_usageStatus_name'),
          buildChartOptions(page.charts, 'as-foregroundRank', 'as_chart_predefined_foregroundRank_name'),
          buildChartOptions(page.charts, 'as-foregroundTimeline', 'as_chart_foregroundTimeline_name'),
          // buildChartOptions(page.charts, 'as-foregroundShift', 'as_chart_foregroundShift_name'),
          buildChartOptions(page.charts, 'as-visitingDevices', 'as_chart_predefined_visitingDevices_name'),
          buildChartOptions(page.charts, 'as-visitTimeline', 'as_chart_visitTimeline_name'),
          buildChartOptions(page.charts, 'as-appVersion', 'as_chart_predefined_appVersion_name'),
          // buildChartOptions(page.charts, 'as-visitShift', 'as_chart_visitShift_name'),
          buildChartOptions(page.charts, 'as-storage', 'as_chart_storage_name'),
          buildChartOptions(page.charts, 'as-dataConnection', 'as_chart_dataConnection_name'),
          buildChartOptions(page.charts, 'as-dataUsage', 'as_chart_dataUsage_name'),
        ],
      },
    ];

    patchCard(page.charts, 'as-deviceStats', card => {
      card.name = 'as_chart_deviceStats_name';
      card.iconTooltip = 'as_chart_deviceStats_full_name';
    });

    patchCard<DonutChartConfig>(page.charts, 'as-manufacturer', chart => {
      chart.iconTooltip = 'as_chartmanufacturer_fullname';
      chart.reportSummary = 'as_chartmanufacturer_reportsummary';
      chart.name = 'as_chart_manufacturer_name';
      chart.elementType = 'chart';
      chart.type = ChartTypeEnum.DONUT;
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.layout = '50p';
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("as_chart_manufacturer_tooltip_label", this.point.name)}`',
        labelArgs: ['as_chart_manufacturer_tooltip_label', 'this.point.name'],
        value:
          '`${xsI18nPipe.transform("as_chart_manufacturer_tooltip_value", this.point.y, (this.point.y/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,\'\'), this.point.devcount)}`',
        valueArgs: [
          'as_chart_manufacturer_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.dimension = {
        dimensionId: 'name',
        label: 'as_chart_manufacturer_dimension',
        noDataLabel: 'no_data_label',
      };
      chart.tag = {
        name: 'as_chart_manufacturer_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.uiSettings = [CHART_UI_DONUT_TOPN, CHART_UI_DONUT_LEAST_MOST];
    });

    patchCard<DonutChartConfig>(page.charts, 'as-usage-status', chart => {
      chart.iconTooltip = 'as_chart_usageStatus_fullname';
      chart.reportSummary = 'as_chart_usageStatus_reportsummary';
      chart.name = 'as_chart_usageStatus_name';
      chart.elementType = 'chart';
      chart.type = ChartTypeEnum.DONUT;
      chart.layout = '50p';
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("as_chart_usage_status_tooltip_label", this.point.name)}`',
        labelArgs: ['as_chart_usage_status_tooltip_label', 'this.point.name'],
        value:
          '`${xsI18nPipe.transform("as_chart_usage_status_tooltip_value", this.point.y, (this.point.y/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,\'\'), this.point.devcount)}`',
        valueArgs: [
          'as_chart_usage_status_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.dimension = {
        dimensionId: 'name',
        label: 'as_chart_usageStatus_dimension',
        noDataLabel: 'no_data_label',
      };
      chart.tag = {
        name: 'as_chart_usageStatus_name',
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
    });

    patchCard<DonutChartConfig>(page.charts, 'as-model', chart => {
      chart.iconTooltip = 'as_chartmodel_fullname';
      chart.reportSummary = 'as_chartmodel_reportsummary';
      chart.name = 'as_chart_model_name';
      chart.elementType = 'chart';
      chart.type = ChartTypeEnum.DONUT;
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.layout = '50p';
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("as_chart_model_tooltip_label", this.point.name)}`',
        labelArgs: ['as_chart_model_tooltip_label', 'this.point.name'],
        value:
          '`${xsI18nPipe.transform("as_chart_model_tooltip_value", this.point.y, (this.point.y/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,\'\'), this.point.devcount)}`',
        valueArgs: [
          'as_chart_model_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.dimension = {
        dimensionId: 'name',
        label: 'as_chart_model_dimension',
        noDataLabel: 'no_data_label',
      };

      chart.tag = {
        name: 'as_chart_model_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.uiSettings = [CHART_UI_DONUT_TOPN, CHART_UI_DONUT_LEAST_MOST];
    });

    patchCard<BarChartConfig>(page.charts, 'as-osVersion', chart => {
      chart.iconTooltip = 'as_chartosVersion_fullname';
      chart.reportSummary = 'as_chartosVersion_reportsummary';
      chart.integerOnly = true;
      chart.name = 'as_chart_osVersion_name';
      chart.elementType = 'chart';
      chart.type = ChartTypeEnum.COLUMN;
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.layout = '50p';

      chart.dimension = {
        dimensionId: 'name',
        label: 'android_ver_label',
        noDataLabel: 'no_data_label',
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.metric = {
        metricId: 'metric',
        label: 'device_count_label',
      };
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("as_chart_osVersion_tooltip_label", this.point.name)}`',
        labelArgs: ['as_chart_osVersion_tooltip_label', 'this.point.name'],
        value:
          '`${xsI18nPipe.transform("as_chart_osVersion_tooltip_value", this.point.y, (this.point.y/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,\'\'), this.point.devcount)}`',
        valueArgs: [
          'as_chart_osVersion_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.uiSettings = [CHART_UI_DONUT_TOPN, CHART_UI_DONUT_LEAST_MOST];

      chart.tag = {
        name: 'as_chart_osVersion_name',
        parameters: ['topN', 'mostLeast'],
      };
    });

    patchCard<BarChartConfig>(page.charts, 'as-foregroundRank', chart => {
      chart.name = 'as_chart_foregroundRank_name';
      chart.iconTooltip = 'as_chart_foregroundRank_fullname';
      chart.reportSummary = 'as_chart_foregroundRank_reportsummary';
      chart.elementType = 'chart';
      chart.type = ChartTypeEnum.COLUMN;
      chart.layout = '50p';
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.dimension = {
        dimensionId: 'label',
        label: 'device_name_label',
        noDataLabel: 'no_data_label',
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.tag = {
        name: 'as_chart_foregroundRank_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.metric = {
        metricId: 'metric',
        label: 'foreground_label',
      };
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("as_chart_foregroundrank_tooltip_label", this.point.label, this.point.mcId)}`',
        labelArgs: ['as_chart_foregroundrank_tooltip_label', 'this.point.label', 'this.point.mcId'],
        value:
          '`${xsI18nPipe.transform("as_chart_foregroundrank_value_tooltip_label", this.point.yDisplay, this.point.totalTimeDisplay, this.point.maxUnit, this.point.timeratio, this.point.devcount)}`',
        valueArgs: [
          'as_chart_foregroundrank_value_tooltip_label',
          'this.point.yDisplay',
          'this.point.totalTimeDisplay',
          'this.point.maxUnit',
          'this.point.timeratio',
          'this.point.devcount',
        ],
      };
      chart.uiSettings = [CHART_UI_BAR_TOPN, CHART_UI_DONUT_LEAST_MOST];
    });

    patchCard<TimelineChartConfig>(page.charts, 'as-foregroundTimeline', chart => {
      chart.iconTooltip = 'as_chart_foregroundTimeline_fullname';
      chart.name = 'as_chart_foregroundTimeline_name';
      chart.timelineColors = [
        {
          label: 'as_foreground_usage_total_usage',
          color: ColorConstants.sotiColors('purple', 70),
        },
      ];
      chart.metric = {
        metricId: 'allY',
        label: 'sdd_app_usage_foreground_metric_label',
      };

      chart.dimension = {
        dimensionId: 'bucketLabel',
        label: 'webapp_chart_time_period_label',
        noDataLabel: 'no_data_label',
      };
      chart.filter = {
        attribute: 'label',
        type: 'mapped',
      };
      chart.tooltip = {
        label: 'as_foreground_usage_total_tooltip_label',
        value: 'as_foreground_usage_tooltip_value',
        dateTime: 'as_foreground_usage_tooltip_date_time',
      };
    });

    // patchChart(page.charts, 'as-foregroundShift', (chart: TimelineChartConfig) => {
    //   chart.name = 'as_chart_foregroundShift_name';
    //   chart.fullName = 'as_chart_foregroundShift_fullname';
    // });
    patchCard<BarChartConfig>(page.charts, 'as-visitingDevices', chart => {
      chart.iconTooltip = 'as_chart_visitingDevices_fullname';
      chart.integerOnly = true;
      chart.name = 'as_chart_visitingDevices_name';
      chart.elementType = 'chart';
      chart.type = ChartTypeEnum.COLUMN;
      chart.layout = '50p';
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.dimension = {
        dimensionId: 'label',
        label: 'device_name_label',
        noDataLabel: 'no_data_label',
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.tag = {
        name: 'as_chart_visitingDevices_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.metric = {
        metricId: 'metric',
        label: 'visit_label',
      };
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("as_visitingDevices_tooltip_label", this.point.label, this.point.mcId)}`',
        labelArgs: ['as_visitingDevices_tooltip_label', 'this.point.label', 'this.point.mcId'],
        value:
          '`${xsI18nPipe.transform("as_visitingDevices_tooltip_value", this.point.y, this.point.totalVisits, this.point.visitratio.toFixed(2).replace(/\\.?0+$/,\'\'), this.point.contribCount)}`',
        valueArgs: [
          'as_visitingDevices_tooltip_value',
          'this.point.y',
          'this.point.totalVisits',
          'this.point.visitratio',
          'this.point.contribCount',
        ],
      };
      chart.uiSettings = [CHART_UI_BAR_TOPN_MANY, CHART_UI_DONUT_LEAST_MOST];
    });

    patchCard<TimelineChartConfig>(page.charts, 'as-visitTimeline', chart => {
      chart.name = 'as_chart_visitTimeline_name';
      chart.integerOnly = true;
      chart.iconTooltip = 'as_chart_visitTimeline_fullname';
      chart.timelineColors = [
        {
          label: 'as_visitTimeline_total',
          color: ColorConstants.sotiColors('yellow', 90),
        },
      ];
      chart.dimension = {
        dimensionId: 'bucketLabel',
        label: 'webapp_chart_time_period_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'allY',
        label: 'as_visitTimeline_total',
      };

      chart.tooltip = {
        label: 'as_visitTimeline_tooltip_label',
        value: 'as_visitTimeline_tooltip_value',
        dateTime: 'as_visitTimeline_tooltip_date_time',
      };
    });

    // patchChart(page.charts, 'as-visitShift', (chart: TimelineChartConfig) => {
    //   chart.name = 'as_chart_visitShift_name';
    //   chart.fullName = 'as_chartvisitShift_fullname';
    // });

    patchCard<BarChartConfig>(page.charts, 'as-appVersion', chart => {
      chart.reportSummary = 'as_chartappVersion_reportsummary';
      chart.iconTooltip = 'as_chartappVersion_fullname';
      chart.integerOnly = true;
      chart.name = 'as_chart_appVersion_name';
      chart.elementType = 'chart';
      chart.type = ChartTypeEnum.COLUMN;
      chart.layout = '50p';
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.dimension = {
        dimensionId: 'name',
        label: 'appVersion_label',
        noDataLabel: 'no_data_label',
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };

      chart.tag = {
        name: 'as_chart_appVersion_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.metric = {
        metricId: 'metric',
        label: 'device_count_label',
      };
      chart.tooltip = {
        label:
          '`${xsI18nPipe.transform("as_chart_appVersion_tooltip_label", this.point.appName,this.point.name, this.point.packageId)}`',
        labelArgs: [
          'as_chart_appVersion_tooltip_label',
          'this.point.appName',
          'this.point.name',
          'this.point.packageId',
        ],
        value:
          '`${xsI18nPipe.transform("as_chart_appVersion_tooltip_value", this.point.y, (this.point.y/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,\'\'), this.point.devcount)}`',
        valueArgs: [
          'as_chart_appVersion_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.uiSettings = [CHART_UI_BAR_TOPN_MANY, CHART_UI_DONUT_LEAST_MOST];
    });

    patchCard<DonutChartConfig>(page.charts, 'as-storage', chart => {
      chart.iconTooltip = 'as_chartstorage_fullname';
      chart.reportSummary = 'as_chartstorage_reportsummary';
      chart.name = 'as_chart_storage_name';
      chart.elementType = 'chart';
      chart.type = ChartTypeEnum.DONUT;
      chart.layout = '50p';
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("as_chart_storage_tooltip_label", this.point.name)}`',
        labelArgs: ['as_chart_storage_tooltip_label', 'this.point.name'],
        value:
          '`${xsI18nPipe.transform("as_chart_storage_tooltip_value", this.point.y, (this.point.y/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,\'\'), this.point.devcount)}`',
        valueArgs: [
          'as_chart_storage_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.dimension = {
        dimensionId: 'name',
        label: 'as_chart_storage_dimension',
        noDataLabel: 'no_data_label',
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.tag = {
        name: 'as_chart_storage_name',
      };
      chart.fixedColors = [
        {
          label: '{StorageSize_6}',
          color: HighchartsConstant.appUsageStaticBucket.other,
        },
        {
          label: '{StorageSize_5}',
          color: HighchartsConstant.appUsageStaticBucket.item5,
        },
        {
          label: '{StorageSize_4}',
          color: HighchartsConstant.appUsageStaticBucket.item4,
        },
        {
          label: '{StorageSize_3}',
          color: HighchartsConstant.appUsageStaticBucket.item3,
        },
        {
          label: '{StorageSize_2}',
          color: HighchartsConstant.appUsageStaticBucket.item2,
        },
        {
          label: '{StorageSize_1}',
          color: HighchartsConstant.appUsageStaticBucket.item1,
        },
      ];
    });

    patchCard<DonutChartConfig>(page.charts, 'as-dataConnection', chart => {
      chart.iconTooltip = 'as_dataConnection_fullname';
      chart.reportSummary = 'as_dataConnection_reportsummary';
      chart.name = 'as_chart_dataConnection_name';
      chart.fullName = 'as_chart_dataConnection_fullname';
      chart.elementType = 'chart';
      chart.type = ChartTypeEnum.DONUT;
      chart.layout = '50p';
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("data_chart_connection_type_tooltip_label", this.point.name)}`',
        labelArgs: ['data_chart_connection_type_tooltip_label', 'this.point.name'],
        value:
          "`${xsI18nPipe.transform('data_chart_connection_type_tooltip_value', this.point.yUnit, this.point.totalUsageUnit,   this.point.usageRatio.toFixed(2).replace(/\\.?0+$/,''),this.point.contributingDevices, ((this.point.contributingDevices/this.point.devcount)*100).toFixed(2).replace(/\\.?0+$/,''), this.point.devcount )}`",
        valueArgs: [
          'data_chart_connection_type_tooltip_value',
          'this.point.yUnit',
          'this.point.totalUsageUnit',
          'this.point.usageRatio',
          'this.point.contributingDevices',
          'percentage(this.point.contributingDevices, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.dimension = {
        dimensionId: 'name',
        label: 'as_chart_dataConnection_dimension',
        noDataLabel: 'no_data_label',
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.tag = {
        name: 'as_chart_dataConnection_name',
      };
      chart.fixedColors = [
        {
          label: '{ConnectionType_2}',
          color: HighchartsConstant.dataUsageStaticBucket.item1,
        },
        {
          label: '{ConnectionType_3}',
          color: HighchartsConstant.dataUsageStaticBucket.item4,
        },
        {
          label: '{ConnectionType_1}',
          color: HighchartsConstant.dataUsageStaticBucket.item2,
        },
      ];
    });

    patchCard<DonutChartConfig>(page.charts, 'as-dataUsage', chart => {
      chart.iconTooltip = 'as_dataUsage_fullname';
      chart.reportSummary = 'as_dataUsage_reportsummary';
      chart.name = 'as_chart_dataUsage_name';
      chart.fullName = 'as_chartdataUsage_fullname';
      chart.elementType = 'chart';
      chart.type = ChartTypeEnum.DONUT;
      chart.layout = '50p';
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("data_chart_usage_upload_tooltip_label", this.point.name)}`',
        labelArgs: ['data_chart_usage_upload_tooltip_label', 'this.point.name'],
        value:
          "`${xsI18nPipe.transform('data_chart_usage_upload_tooltip_value', this.point.yUnit, this.point.totalUsageUnit,   this.point.usageRatio.toFixed(2).replace(/\\.?0+$/,''), this.point.contributedCount ,((this.point.contributedCount /this.point.devcount)*100).toFixed(2).replace(/\\.?0+$/,''), this.point.devcount )}`",
        valueArgs: [
          'data_chart_usage_upload_tooltip_value',
          'this.point.yUnit',
          'this.point.totalUsageUnit',
          'this.point.usageRatio',
          'this.point.contributedCount',
          'percentage(this.point.contributedCount, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.dimension = {
        dimensionId: 'name',
        label: 'as_chartdataUsage_dimension',
        noDataLabel: 'no_data_label',
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.tag = {
        name: 'as_chart_dataUsage_name',
      };
      chart.fixedColors = [
        {
          label: '{DATA_UPLOAD}',
          color: HighchartsConstant.dataUsageStaticBucket.item1,
        },
        {
          label: '{DATA_DOWNLOAD}',
          color: HighchartsConstant.dataUsageStaticBucket.item2,
        },
      ];
    });

    patchChart(page.charts, 'as-deviceTable', (chart: TableConfig) => {
      chart.name = 'as_chart_deviceTable_name';
      chart.elementType = 'table';
      chart.selectionType = InsSelectionType.multiple;
      chart.selectionText = 'device_selected_label';
      chart.selectionTextPlural = 'device_selected_label';
      chart.rowActions = [
        {
          action: 'customAttributes',
          tooltip: 'custom_attr_title',
          icon: 'insight-icon-deviceinfo',
        },
      ];
      chart.filters = {
        sortAttribute: 'McDeviceName',
        sortDirection: 'asc',
        pageSize: 50,
        pageSkip: 0,
      };

      chart.tableAttributes = [
        {
          id: 'McDeviceName',
          label: 'aps_table_device_mcDeviceName_label',
          renderer: TableRenderEnum.LINK,
          rendererParams: {
            eventId: 'deviceClicked',
            icons: [
              {
                iconClass: 'insight-icon-type-DeviceAndroidGeneric',
              },
            ],
            eventParams: {
              nested: true,
              targetAppBaselineId: { ANDROID: DEVICE_APP, WINDOWS: DEVICE_APP_W },
              targetPageBaselineId: { ANDROID: DEVICE_LIST, WINDOWS: DEVICE_LIST_W },
            },
          },
        },
        {
          id: 'McDeviceId',
          label: 'Column_Name_Device_Id',
          renderer: TableRenderEnum.STRING,
        },
        {
          id: 'GroupName',
          label: 'Column_Name_Device_Group',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'GroupPath',
          label: 'group_path_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'Model',
          label: 'Column_Name_Model',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'Manufacturer',
          label: 'label_Manufacturer',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'OSVersion',
          label: 'Column_Name_Os_Version',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
          primaryColumnSortId: 'OSVersionAsPaddedString',
        },
        {
          id: 'AverageVisitTime',
          label: 'as_chart_deviceTable_AverageVisitTime_label',
          renderer: TableRenderEnum.NUMBER_FORMAT,
          rendererParams: { fromUnit: 'seconds', toUnit: 'DYNAMIC', decimals: 2 },
          iconClass: '',
        },
        {
          id: 'timesLaunched',
          label: 'times_launched_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'consumedTimeInMillisecs',
          label: 'foreground_label',
          renderer: TableRenderEnum.TIME,
          iconClass: '',
          primaryColumnSortId: 'consumedTimeInMillisecs',
        },
        {
          id: 'appVersion',
          label: 'appVersion_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
      ];
    });
  });

  const pageIds = [findPageId(app.pages, APP_SPOTLIGHT_LIST_PAGE)];

  const childPageIds = [findPageId(app.pages, APP_SPOTLIGHT_INFO_PAGE)];

  const defaultTabId = findPageId(app.pages, APP_SPOTLIGHT_INFO_PAGE);

  return { ...app, pageIds, childPageIds, defaultTabId };
}
